/**
 * Utility to print a Booking Agreement / Provisional Transfer Letter
 * @param {Object} data - Contains plot, customer, and booking details
 */
const printAgreement = (data) => {
    const { agreement } = data;
    const plot = agreement.plot;
    const customer = agreement.customer;
    const society = agreement.society || plot?.society_id;

    const societyName = society?.name || "AL NOOR DEVELOPERS";
    const societyLocation = society?.location || "Lahore, Pakistan";

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-PK", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-PK", {
            style: "currency",
            currency: "PKR",
            minimumFractionDigits: 0
        }).format(amount || 0).replace("PKR", "Rs.");
    };

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
    <html>
      <head>
        <title>Agreement - ${plot?.plot_number || "N/A"}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
          body {
            font-family: 'Inter', sans-serif;
            line-height: 1.5;
            color: #1e293b;
            margin: 0;
            padding: 50px;
            background: #fff;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #0f172a;
          }
          .society-name {
            font-size: 32px;
            font-weight: 700;
            color: #0d9488;
            margin: 0;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .society-loc {
            font-size: 14px;
            color: #64748b;
            margin: 5px 0 0 0;
          }
          .doc-title {
            text-align: center;
            font-size: 22px;
            font-weight: 700;
            margin: 25px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #0f172a;
            border: 2px solid #0f172a;
            padding: 10px;
            display: inline-block;
            width: 80%;
            margin-left: 10%;
          }
          .meta-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            font-size: 14px;
          }
          .section {
            margin-bottom: 25px;
          }
          .section-title {
            font-size: 14px;
            font-weight: 700;
            color: #fff;
            background: #0f172a;
            padding: 8px 15px;
            text-transform: uppercase;
            margin-bottom: 15px;
            border-radius: 4px;
          }
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
          .info-item {
            display: flex;
            border-bottom: 1px dotted #cbd5e1;
            padding-bottom: 5px;
            margin-bottom: 10px;
            font-size: 14px;
          }
          .label {
            color: #64748b;
            font-weight: 600;
            width: 140px;
            flex-shrink: 0;
          }
          .value {
            font-weight: 400;
            color: #1e293b;
          }
          .financials {
            background: #f8fafc;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #e2e8f0;
          }
          .total-row {
            background: #0f172a;
            color: #fff;
            padding: 12px 15px;
            border-radius: 4px;
            margin-top: 15px;
            display: flex;
            justify-content: space-between;
            font-weight: 700;
          }
          .footer {
            margin-top: 80px;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 40px;
          }
          .signature-box {
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #0f172a;
            margin-bottom: 8px;
          }
          .terms {
            font-size: 11px;
            color: #64748b;
            margin-top: 40px;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
          @media print {
            body { padding: 30px; }
            .section-title { background: #0f172a !important; -webkit-print-color-adjust: exact; }
            .total-row { background: #0f172a !important; -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="society-name">${societyName}</h1>
          <p class="society-loc">${societyLocation}</p>
        </div>

        <div class="meta-info">
          <div><strong>Booking Ref:</strong> ${agreement.booking_number || "N/A"}</div>
          <div><strong>Date:</strong> ${formatDate(new Date())}</div>
        </div>

        <div class="doc-title">Provisional Booking Agreement / Transfer Letter</div>

        <div class="section">
          <h3 class="section-title">Party Information</h3>
          <div class="grid">
            <div>
              <div class="info-item"><span class="label">Primary Member:</span> <span class="value">${customer?.name || "N/A"}</span></div>
              <div class="info-item"><span class="label">S/O, D/O, W/O:</span> <span class="value">${customer?.contactInfo?.father_name || "N/A"}</span></div>
              <div class="info-item"><span class="label">CNIC / ID:</span> <span class="value">${customer?.contactInfo?.cnic || "N/A"}</span></div>
            </div>
            <div>
              <div class="info-item"><span class="label">Contact No:</span> <span class="value">${customer?.contactInfo?.phone || "N/A"}</span></div>
              <div class="info-item"><span class="label">Address:</span> <span class="value">${customer?.contactInfo?.address || "N/A"}</span></div>
            </div>
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Property Details</h3>
          <div class="grid">
            <div>
              <div class="info-item"><span class="label">Plot Number:</span> <span class="value">${plot?.plot_number || "N/A"}</span></div>
              <div class="info-item"><span class="label">Block:</span> <span class="value">${plot?.block || plot?.block_number || "N/A"}</span></div>
              <div class="info-item"><span class="label">Property Size:</span> <span class="value">${plot?.size || plot?.marla + " Marla" || "N/A"}</span></div>
            </div>
            <div>
              <div class="info-item"><span class="label">Category:</span> <span class="value">${plot?.category || "Standard"}</span></div>
              <div class="info-item"><span class="label">Booking Date:</span> <span class="value">${formatDate(agreement.booking_date)}</span></div>
            </div>
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Financial Summary</h3>
          <div class="financials">
            <div class="info-item"><span class="label">Total Plot Value:</span> <span class="value">${formatCurrency(agreement.total_amount)}</span></div>
            <div class="info-item"><span class="label">Initial Downpayment:</span> <span class="value">${formatCurrency(agreement.initial_payment)}</span></div>
            <div class="info-item"><span class="label">Payment Plan:</span> <span class="value">${agreement.payment_mode} ${agreement.payment_mode === 'Installment' ? '(' + (agreement.installment_years || 0) + 'Y ' + (agreement.installment_months || 0) + 'M)' : ''}</span></div>
            
            <div class="total-row">
              <span>OUTSTANDING BALANCE</span> 
              <span>${formatCurrency(agreement.remaining_balance)}</span>
            </div>
          </div>
        </div>

        <div class="terms">
          <strong>Terms & Conditions:</strong>
          <ol>
            <li>This agreement is provisional and subject to the fulfillment of regular installments as per the agreed plan.</li>
            <li>Default in payments for three consecutive months may lead to cancellation after due notice.</li>
            <li>Possession and final transfer will only be granted upon 100% payment clearance.</li>
            <li>All taxes and government duties shall be borne by the member.</li>
          </ol>
        </div>

        <div class="footer">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="label">Member Signature</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="label">Authorized Dealer</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="label">Director Accounts</div>
          </div>
        </div>

        <script>
          window.onafterprint = function() { window.close(); };
          setTimeout(() => { window.print(); }, 500);
        </script>
      </body>
    </html>
  `);
    printWindow.document.close();
};

export default printAgreement;
